import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { User } from 'src/app/classes/user';
import { Environment } from 'src/app/classes/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserProfileImageService } from 'src/app/services/user-profile-image.service';
import { AvatarsService } from 'src/app/services/avatars.service';
import { filter, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';
import type { AvatarSize } from '../avatar-circle/avatar-circle.component';

@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit {

	user = new User();
	profileImageUrl: string | null = null;
	hasCustomImage = false;

	avatarSize: AvatarSize = 'lg';
	private readonly borderPx = 3;
	
	get avatarPx(): number {
		switch (this.avatarSize) {
			case 'xs': return 24;
			case 'sm': return 32;
			case 'md': return 48;
			case 'lg': return 96;
			default: return 48;
		}
	}
    get innerAvatarPx(): number {
    	return Math.max(this.avatarPx - 2 * this.borderPx, 0);
	}

	get spinnerDiameter(): number {
		return Math.max(this.avatarPx - 6, 16);
	}      

	// cache-buster für <app-avatar-circle> (falls das Web Component das Bild intern cached)
	avatarCacheBuster = Date.now();

	txtRole: { [key: string]: string } = {
		MITGLIED: "Mitglied",
		ADMIN: "Administrator",
		MITGLIED_ADMIN: "Mitglied Administrator",
		MITGLIED_DATENPFLEGE: "Datenpflege",
	}

	// Lokale Validierung vor Upload (Backend resized ohnehin auf ~750KB + 50KB Thumb)
	private readonly maxImageSizeBytes = 3 * 1024 * 1024;
	private readonly allowedExtensions = new Set(['jpe', 'jpeg', 'jpg', 'gif', 'png']);
	private readonly allowedMimeTypes = new Set(['image/jpeg', 'image/jpg', 'image/gif', 'image/png']);
	private readonly targetImageSizePx = 225;
	private destroy$ = new Subject<void>();
	isUploading = false;
	private lastGoodImageUrl: string | null = null;
	isImageLoading = true;


	constructor(
		private authService: AuthenticationService,
		@Inject('environment') public environment: Environment,
	private snackBar: MatSnackBar,
	private userProfileImageService: UserProfileImageService,
	private avatarsService: AvatarsService,
	) { }

	ngOnInit() {
		this.user = this.authService.getCurrentUser();
		this.loadProfileImageFromBackend({ cacheBust: true, keepOldOnNull: true });
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadProfileImageFromBackend(opts?: { cacheBust?: boolean; keepOldOnNull?: boolean }) {
		const ebeId = this.user?.ebeId;
		if (!ebeId) {
			this.profileImageUrl = null;
			this.lastGoodImageUrl = null;
			this.hasCustomImage = false;
			this.isImageLoading = false;
			return;
		}

		this.isImageLoading = true;
		this.userProfileImageService.getFull(ebeId, { cacheBust: !!opts?.cacheBust })
			.pipe(finalize(() => { this.isImageLoading = false; }))
			.subscribe(blob => {
				if (!blob) {
					// Optional: altes Bild behalten statt Fallback, um Flackern zu vermeiden
					if (!opts?.keepOldOnNull) {
						this.profileImageUrl = null;
						this.lastGoodImageUrl = null;
						this.hasCustomImage = false;
					}
					this.isImageLoading = false;
					return;
				}
				this.blobToDataUrl(blob).then(dataUrl => {
					this.profileImageUrl = dataUrl;
					this.lastGoodImageUrl = null;
					this.hasCustomImage = true;
					this.bumpAvatarCacheBuster();
					this.isImageLoading = false;
				}).catch(() => {
					if (!opts?.keepOldOnNull) {
						this.profileImageUrl = null;
						this.lastGoodImageUrl = null;
						this.hasCustomImage = false;
					}
					this.isImageLoading = false;
				});
			});
	}

	openFilePicker(input: HTMLInputElement) {
		input.value = '';
		input.click();
	}

	onProfileImageSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files && input.files[0];
		input.value = '';
		if (!file) return;

		if (!file.type.startsWith('image/')) {
			this.snackBar.open('Bitte eine Bilddatei auswählen.', '', { duration: 4000 });
			return;
		}
		if (file.size > this.maxImageSizeBytes) {
			this.snackBar.open('Das Bild darf maximal 3 MB groß sein.', '', { duration: 4000 });
			return;
		}

		const ebeId = this.user?.ebeId;
		if (!ebeId) return;

		// keine Preview hier! nur UI sperren
		this.isUploading = true;

		this.resizeImageToSquare(file, this.targetImageSizePx).then(resizedFile => {
			this.userProfileImageService.upload(ebeId, resizedFile).subscribe({
				next: (ok) => {
					if (ok) {
						this.snackBar.open('Profilbild hochgeladen. Finalisiere ...', '', { duration: 2000 });
						this.avatarsService.invalidate(ebeId);
						this.avatarsService.ensureMinis([ebeId]).pipe(take(1)).subscribe();
						this.pollFullImageUntilAvailable(ebeId); // lädt erst nach Backend-OK
					} else {
						this.snackBar.open('Unzulässiger Dateityp. Bitte nur Dateien mit der Endung: ".jpe, .jpeg, .jpg, .gif oder .png" wählen.', '', { duration: 8000 });
						this.isUploading = false;
					}
				},
				error: () => {
					this.snackBar.open('Upload fehlgeschlagen.', '', { duration: 4000 });
					this.isUploading = false;
				}
			});
		}).catch(() => {
			this.snackBar.open('Das Bild konnte nicht verarbeitet werden.', '', { duration: 4000 });
			this.isUploading = false;
		});
	}

	removeProfileImage() {
		const ebeId = this.user?.ebeId;
		if (!ebeId) return;

		this.userProfileImageService.remove(ebeId).subscribe({
			next: (ok) => {
				if (ok) {
					this.profileImageUrl = null;
					this.hasCustomImage = false;
					this.bumpAvatarCacheBuster();
					this.isImageLoading = false;
					this.avatarsService.invalidate(ebeId);
					this.avatarsService.ensureMinis([ebeId]).pipe(take(1)).subscribe();
					this.snackBar.open('Profilbild entfernt.', '', { duration: 3000 });
				} else {
					this.snackBar.open('Entfernen fehlgeschlagen.', '', { duration: 4000 });
				}
			},
			error: () => {
				this.snackBar.open('Entfernen fehlgeschlagen.', '', { duration: 4000 });
			}
		});
	}

	private pollFullImageUntilAvailable(ebeId: string) {
		this.isImageLoading = true;
		timer(0, 600).pipe(
			take(10),
			switchMap(() => this.userProfileImageService.getFull(ebeId, { cacheBust: true })),
			filter((blob): blob is Blob => !!blob),
			take(1),
			takeUntil(this.destroy$)
		).subscribe({
			next: (blob) => {
				this.blobToDataUrl(blob).then(url => {
					this.profileImageUrl = url;        // erst jetzt anzeigen
					this.lastGoodImageUrl = url;       // confirmed version
					this.hasCustomImage = true;
					this.bumpAvatarCacheBuster();
					this.isUploading = false;
					this.isImageLoading = false;
					this.snackBar.open('Profilbild aktualisiert.', '', { duration: 2000 });
				}).catch(() => {
					this.isImageLoading = false;
				});
			},
			complete: () => {
				if (this.isUploading) {
					this.snackBar.open('Server-Bild nicht verfuegbar. Es bleibt das alte Bild.', '', { duration: 4000 });
					this.isUploading = false;
				}
				this.isImageLoading = false;
			}
		});
	}

	/** Hilfsfunktion: Blob -> data:URL */
	private blobToDataUrl(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const fr = new FileReader();
			fr.onload = () => typeof fr.result === 'string' ? resolve(fr.result) : reject();
			fr.onerror = reject;
			fr.readAsDataURL(blob);
		});
	}

	/** Cache-Buster für Web Component triggern */
	private bumpAvatarCacheBuster() {
		this.avatarCacheBuster = Date.now();
	}

	private resizeImageToSquare(file: File, targetSize: number): Promise<File> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onerror = () => reject(new Error('file-read-error'));
			reader.onload = () => {
				if (typeof reader.result !== 'string') {
					reject(new Error('file-read-result'));
					return;
				}

				const img = new Image();
				img.onerror = () => reject(new Error('image-load-error'));
				img.onload = () => {
					const canvas = document.createElement('canvas');
					canvas.width = targetSize;
					canvas.height = targetSize;
					const ctx = canvas.getContext('2d');
					if (!ctx) {
						reject(new Error('canvas-context-error'));
						return;
					}

					const srcSize = Math.min(img.width, img.height);
					const sx = (img.width - srcSize) / 2;
					const sy = (img.height - srcSize) / 2;

					ctx.clearRect(0, 0, targetSize, targetSize);
					ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, targetSize, targetSize);

					const mimeType = this.getTargetMimeType(file.type);
					canvas.toBlob(blob => {
						if (!blob) {
							reject(new Error('canvas-blob-error'));
							return;
						}
						resolve(new File([blob], file.name, { type: mimeType }));
					}, mimeType);
				};
				img.src = reader.result;
			};
			reader.readAsDataURL(file);
		});
	}

	private getTargetMimeType(originalType: string): string {
		const lower = (originalType || '').toLowerCase();
		if (lower === 'image/jpeg' || lower === 'image/jpg') return 'image/jpeg';
		if (lower === 'image/png') return 'image/png';
		return 'image/png';
	}
}



